/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {ForwardedRef, forwardRef, useContext, useImperativeHandle, useRef, useState} from 'react';

import {ActivateHandle, DashboardPeriod, DataSplitMethod, EmailFlow, EmailStepStat} from '../../../util/interfaces';

import {ResponseData} from '@dvrd/dvr-controls';

import {getEmailStepStats, getGlobalEmailPeriodStats} from '../../../util/requestSender';

import {createErrorMessage, getDashboardPeriod, saveDashboardPeriod} from '../../../util/utils';

import EmailStepStats from './emailStepStats';

import {AppContext} from '../../misc/appContext';

import {getFromLocal, saveToLocal} from '../../../util/storageUtil';

import EmailStep from '../../../models/emailStepModel';

import {showDialog} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

const LOCAL_STORAGE_PERIOD_KEY = 'bolmate-email-stat-period',
    LOCAL_STORAGE_PERIOD_START_KEY = 'bolmate-email-stat-start',
    LOCAL_STORAGE_PERIOD_END_KEY = 'bolmate-email-stat-end',
    LOCAL_STORAGE_DISPLAY_KEY = 'bolmate-email-stat-display';

function getInitialPeriod(): DashboardPeriod | null {
    return getDashboardPeriod(
        undefined,
        LOCAL_STORAGE_PERIOD_KEY,
        LOCAL_STORAGE_PERIOD_START_KEY,
        LOCAL_STORAGE_PERIOD_END_KEY,
        true
    );
}

function EmailStepStatsController(props: any, ref: ForwardedRef<ActivateHandle>) {
    const {account} = useContext(AppContext).accountContext;
    const [stats, setStats] = useState<EmailStepStat[]>([]);
    const [loading, setLoading] = useState(false);
    const [period, setPeriod] = useState<DashboardPeriod | null>(getInitialPeriod());
    const [active, setActive] = useState(false);
    const [splitMethod, setSplitMethod] = useState<DataSplitMethod>(
        (getFromLocal(LOCAL_STORAGE_DISPLAY_KEY) as DataSplitMethod | null) ?? DataSplitMethod.QUARTER
    );
    const [showColors, setShowColors] = useState(true);
    const emailStepId = useRef<string | null>(null);
    const {t} = useTranslation();

    function activate(step?: EmailStep) {
        emailStepId.current = step?.id ?? null;
        setShowColors([EmailFlow.REVIEW, EmailFlow.REVIEW_INVOICE, undefined].includes(step?.flow));
        const period = getDashboardPeriod(
                undefined,
                LOCAL_STORAGE_PERIOD_KEY,
                LOCAL_STORAGE_PERIOD_START_KEY,
                LOCAL_STORAGE_PERIOD_END_KEY,
                true
            ),
            splitMethod =
                (getFromLocal(LOCAL_STORAGE_DISPLAY_KEY) as DataSplitMethod | null) ?? DataSplitMethod.QUARTER;
        setPeriod(period);
        setSplitMethod(splitMethod);
        setActive(true);
        loadStats(period, splitMethod);
    }

    function onChangePeriod(period: DashboardPeriod) {
        saveDashboardPeriod(
            period,
            LOCAL_STORAGE_PERIOD_KEY,
            LOCAL_STORAGE_PERIOD_START_KEY,
            LOCAL_STORAGE_PERIOD_END_KEY
        );
        setPeriod(period);
        loadStats(period, splitMethod);
    }

    function onChangeSplitMethod(splitMethod: DataSplitMethod) {
        saveToLocal(LOCAL_STORAGE_DISPLAY_KEY, splitMethod);
        setSplitMethod(splitMethod);
        loadStats(period, splitMethod);
    }

    function onClose() {
        setActive(false);
        setLoading(false);
        setStats([]);
        emailStepId.current = '';
    }

    function loadStats(period: DashboardPeriod | null, splitMethod: DataSplitMethod) {
        if (!account) return;
        setLoading(true);
        if (emailStepId.current) loadStepStats(period, splitMethod);
        else loadGlobalStats(period, splitMethod);
    }

    function loadStepStats(period: DashboardPeriod | null, splitMethod: DataSplitMethod) {
        getEmailStepStats({
            data: {
                email_step_id: emailStepId.current!,
                period_end: period?.periodEnd?.format('YYYY-MM-DD'),
                period_start: period?.periodStart?.format('YYYY-MM-DD'),
                split_method: splitMethod,
                account_id: account!.id
            },
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) setStats(data.counts);
                else
                    showDialog(
                        createErrorMessage(
                            data.message ?? t('email.stats.loadFailed', 'Het laden van de statistieken is niet gelukt.')
                        ),
                        t('email.stats.loadFailedTitle', 'Laden mislukt')
                    );
            }
        });
    }

    function loadGlobalStats(period: DashboardPeriod | null, splitMethod: DataSplitMethod) {
        getGlobalEmailPeriodStats({
            data: {
                period_end: period?.periodEnd?.format('YYYY-MM-DD'),
                period_start: period?.periodStart?.format('YYYY-MM-DD'),
                split_method: splitMethod,
                account_id: account!.id
            },
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) setStats(data.counts);
                else
                    showDialog(
                        createErrorMessage(
                            data.message ?? t('email.stats.loadFailed', 'Het laden van de statistieken is niet gelukt.')
                        ),
                        t('email.stats.loadFailedTitle', 'Laden mislukt')
                    );
            }
        });
    }

    useImperativeHandle(ref, () => ({activate}));

    return (
        <EmailStepStats
            onClose={onClose}
            onChangePeriod={onChangePeriod}
            onChangeSplitMethod={onChangeSplitMethod}
            stats={stats}
            active={active}
            loading={loading}
            period={period}
            splitMethod={splitMethod}
            showColors={showColors}
        />
    );
}

export default forwardRef<ActivateHandle, any>(EmailStepStatsController);
